import { Box, Typography, useTheme, alpha } from '@mui/material';
import { PropsWithChildren, ReactNode, useEffect } from 'react';
import Logo from '../sidebar/Logo';
import AuthFooter from './AuthFooter';

interface AuthLayoutProps extends PropsWithChildren {
    title?: string;
    subtitle?: ReactNode;
}

export default function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
    const theme = useTheme();

    useEffect(() => {
        let containerFrameId: number | null = null;
        let bannerCheckIntervalId: number | null = null;
        let containerFindAttempts = 0;
        const maxContainerFindAttempts = 5; // How many times to try finding the main container

        const performPaddingAdjustmentLogic = (containerElement: HTMLElement) => {
            const shadowHost = document.getElementById('attribution-widget');
            let celloBanner = null;
            if (shadowHost && shadowHost.shadowRoot) {
                celloBanner = shadowHost.shadowRoot.getElementById('cello-banner');
            } else {
                // If shadowHost or shadowRoot doesn't exist, we can assume the banner isn't there or ready
                celloBanner = null;
            }

            if (celloBanner) {
                if (containerElement.style.paddingTop !== '120px') {
                    containerElement.style.paddingTop = '120px';
                }
            } else {
                if (containerElement.style.paddingTop !== '') {
                    containerElement.style.paddingTop = ''; // Revert to sx or default
                }
            }
        };

        const findContainerAndStartBannerChecks = () => {
            const container = document.getElementById('auth-layout-container1');

            if (container) {
                // Container found. Perform initial check immediately.
                performPaddingAdjustmentLogic(container);

                // Then, set up an interval to repeatedly check banner status for a few seconds.
                let bannerCheckCount = 0;
                const maxBannerChecks = 12; // e.g., 12 checks * 250ms = 3 seconds
                const bannerCheckIntervalMs = 250; // Check every 250ms (total 5 seconds)

                bannerCheckIntervalId = window.setInterval(() => {
                    bannerCheckCount++;
                    if (!document.body.contains(container) || bannerCheckCount > maxBannerChecks) {
                        // Stop if container is removed or max checks reached
                        if (bannerCheckIntervalId) clearInterval(bannerCheckIntervalId);
                        return;
                    }
                    performPaddingAdjustmentLogic(container);
                }, bannerCheckIntervalMs);

            } else {
                containerFindAttempts++;
                if (containerFindAttempts < maxContainerFindAttempts) {
                    containerFrameId = requestAnimationFrame(findContainerAndStartBannerChecks);
                }
            }
        };

        findContainerAndStartBannerChecks();

        return () => {
            if (containerFrameId) {
                cancelAnimationFrame(containerFrameId);
            }
            if (bannerCheckIntervalId) {
                clearInterval(bannerCheckIntervalId);
            }
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

 
 return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            position: 'relative',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                padding: { xs: '16px', md: '24px' }
            }}
            id="auth-layout-container1"
            >

                <Box sx={{
                    // Mobile: full width without Paper styling
                    // Desktop: Paper-like styling with max width
                    padding: { xs: '32px 16px', md: '60px 80px' },
                    width: '100%',
                    maxWidth: { xs: '100%', md: '550px' },
                    display: 'flex',
                    flexDirection: 'column',
                    // Paper styling only on desktop
                    border: { xs: 'none', md: '1px solid' },
                    borderColor: { xs: 'transparent', md: theme.palette.divider },
                    borderRadius: { xs: 0, md: 1 },
                    boxShadow: { xs: 'none', md: '0 1px 3px rgba(0,0,0,0.05)' },
                    bgcolor: { xs: 'transparent', md: alpha(theme.palette.background.paper, 0.95) },
                }}>
                {(title || subtitle) && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        gap: { xs: 3, sm: 4 },
                        mb: 4,
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                            <Logo width={64} height={64} />
                        </Box>
                        {(title || subtitle) && (
                            <Box>
                                {title && (
                                    <Typography variant="h4" component="h1" sx={{
                                        fontWeight: 600,
                                        fontSize: '2.2rem',
                                        lineHeight: 1.2,
                                        color: 'text.primary'
                                    }}>
                                        {title}
                                    </Typography>
                                )}
                                {subtitle && (
                                    <Typography variant="body1" component="div" sx={{ color: 'text.secondary', mt: 1 }}>
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
                {children}
                </Box>
            </Box>
            <Box sx={{ 
                width: '100%', 
                backgroundColor: theme.palette.background.default,
                padding: { xs: '16px', md: '24px' },
                mt: 'auto'
            }}>
                <AuthFooter />
            </Box>
        </Box>
    );
}
